import { useState } from "react";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import { useFormik } from "formik";
import * as Yup from "yup";
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import PublicLayout from "sections/PublicLayout";
import LocationMap from "sections/public/LocationMap";
import { BRANCHES } from "sections/public/branches";
import { submitEnquiry } from "firestoreService";

const CONTACT_DETAILS = [
  {
    icon: PhoneIcon,
    label: "Phone",
    lines: ["+91 9868420099", "+91 8076311592", "+91 8920556391", "+91 9899795709"],
  },
  { icon: EmailIcon, label: "Email", lines: ["murlidharjha43@gmail.com"] },
  {
    icon: PlaceIcon,
    label: "Branches",
    // Read from the same list the map uses, so the two cannot disagree.
    lines: BRANCHES.map((branch) => `${branch.label}: ${branch.address}`),
  },
];

// The page shipped a <form method="post"> with no fields and no action: a
// visitor had nothing to fill in, and there was nowhere for it to go. The
// form below writes to the `enquiries` collection the admin inbox reads.
function ContactUs() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const validation = useFormik({
    initialValues: { name: "", email: "", mobileNo: "", subject: "", message: "" },
    validationSchema: Yup.object().shape({
      // These limits mirror firestore.rules exactly, so a submission that
      // passes here cannot be rejected by the server for length.
      name: Yup.string().trim().max(120, "Name is too long").required("Name is required"),
      email: Yup.string()
        .trim()
        .email("Enter a valid email address")
        .max(200, "Email is too long")
        .required("Email is required"),
      mobileNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a 10 digit mobile number")
        .required("Mobile number is required"),
      subject: Yup.string().trim().max(150, "Subject is too long").required("Subject is required"),
      message: Yup.string()
        .trim()
        .max(2000, "Message is too long")
        .required("Please tell us how we can help"),
    }),
    onSubmit: async (values, helpers) => {
      setError("");
      try {
        await submitEnquiry({
          name: values.name.trim(),
          email: values.email.trim(),
          mobileNo: values.mobileNo.trim(),
          subject: values.subject.trim(),
          message: values.message.trim(),
        });
        helpers.resetForm();
        setSent(true);
      } catch (submitError) {
        console.error("Could not send enquiry:", submitError);
        setError("We could not send your message. Please try again, or call us directly.");
      }
    },
  });

  const field = (name, label, extra = {}) => (
    <>
      <TextField
        fullWidth
        size="small"
        label={label}
        name={name}
        value={validation.values[name]}
        onChange={validation.handleChange}
        onBlur={validation.handleBlur}
        error={Boolean(validation.touched[name] && validation.errors[name])}
        {...extra}
      />
      {validation.touched[name] && validation.errors[name] ? (
        <FormHelperText sx={{ color: "#f44335" }}>{validation.errors[name]}</FormHelperText>
      ) : null}
    </>
  );

  return (
    <PublicLayout
      eyebrow="Get in touch"
      title="Contact Us"
      subtitle="Looking for a home tutor, or want to teach with us? Send us a message."
    >
      <Container>
        <Grid container spacing={4} sx={{ py: { xs: 3, md: 5 } }}>
          <Grid item xs={12} md={5}>
            <MKTypography variant="h4" mb={3}>
              Reach us directly
            </MKTypography>

            <Stack spacing={3}>
              {CONTACT_DETAILS.map(({ icon: Icon, label, lines }) => (
                <Stack key={label} direction="row" spacing={2} alignItems="flex-start">
                  <MKBox
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      bgcolor: ({ palette: { brand } }) => brand.mist,
                    }}
                  >
                    <Icon sx={{ color: ({ palette: { brand } }) => brand.navy }} />
                  </MKBox>
                  <MKBox sx={{ minWidth: 0 }}>
                    <MKTypography variant="h6" sx={{ fontSize: "0.95rem" }}>
                      {label}
                    </MKTypography>
                    {lines.map((line) => (
                      <MKTypography key={line} variant="button" color="text" display="block">
                        {line}
                      </MKTypography>
                    ))}
                  </MKBox>
                </Stack>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card
              sx={{
                p: { xs: 2.5, md: 4 },
                borderRadius: 3,
                boxShadow: "none",
                border: ({ palette: { brand } }) => `1px solid ${brand.line}`,
              }}
            >
              <MKTypography variant="h4" mb={0.5}>
                Send us a message
              </MKTypography>
              <MKTypography variant="button" color="text" mb={3} display="block">
                We usually reply within one working day.
              </MKTypography>

              {sent && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSent(false)}>
                  Thank you — your message has reached us. We will be in touch shortly.
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={validation.handleSubmit} noValidate>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    {field("name", "Your name")}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {field("mobileNo", "Mobile number", { inputProps: { maxLength: 10 } })}
                  </Grid>
                  <Grid item xs={12}>
                    {field("email", "Email address", { type: "email" })}
                  </Grid>
                  <Grid item xs={12}>
                    {field("subject", "Subject")}
                  </Grid>
                  <Grid item xs={12}>
                    {field("message", "How can we help?", { multiline: true, rows: 5 })}
                  </Grid>
                  <Grid item xs={12}>
                    <MKButton
                      type="submit"
                      variant="gradient"
                      color="primary"
                      fullWidth
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? "Sending…" : "Send message"}
                    </MKButton>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <LocationMap />
    </PublicLayout>
  );
}

export default ContactUs;
